export function formatCurrency(amount: number, currency = 'INR'): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

/** Indian short scale — 100000 → "1L", 40000 → "40k", 1_00_00_000 → "1Cr" — instead of printing
 * every digit. Up to 2 decimal places, trailing zeros trimmed (12L, 12.5L, 12.75L — never 12.00L or
 * 12.50L), the standard precision Indian fintech apps use for a compact figure. No ₹ symbol
 * (callers prepend one themselves where the figure needs it standalone) — this keeps the original
 * behavior byte-identical for its first caller, `echarts-bar-chart.tsx`'s axis ticks, where the
 * symbol would be redundant clutter next to an already-currency chart. Promoted here (was private
 * to that file) so large headline figures (a hero's portfolio value, a stat card's currency total)
 * can reuse the same abbreviation, each pairing it with the full `formatCurrency` value in a `title`
 * attribute so hovering still reveals the exact number. Deliberately opt-in per call site, never
 * automatic by magnitude — callers decide whether a given value is "currency that can be
 * abbreviated" at all, since the identical `MetricCard` is also used for plain counts that must
 * never be abbreviated. */
export function formatCompactCurrency(value: number): string {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  const trimmed = (n: number) => n.toFixed(2).replace(/\.?0+$/, '')
  if (abs >= 1_00_00_000) return `${sign}${trimmed(abs / 1_00_00_000)}Cr`
  if (abs >= 1_00_000) return `${sign}${trimmed(abs / 1_00_000)}L`
  if (abs >= 1_000) return `${sign}${trimmed(abs / 1_000)}k`
  return `${sign}${abs}`
}

/** For a standalone headline figure (a hero's portfolio value, a stat card's currency total) —
 * unlike a chart axis tick, where compactness matters even at a few thousand, a headline reading
 * "₹15,000" is already perfectly clear on its own; abbreviating it to "15k" would lose more clarity
 * than space it saves. Only abbreviates from the Lakh tier up, where a figure's digit count
 * actually risks overflowing a card; below that, returns the same exact, comma-grouped
 * `formatCurrency`. Pass the absolute value in — callers with a signed figure (e.g. gain/loss)
 * prepend their own "+"/"−" to both this and the `formatCurrency` value used as the `title`. */
export function formatHeadlineCurrency(absoluteAmount: number): string {
  return absoluteAmount >= 1_00_000
    ? `₹${formatCompactCurrency(absoluteAmount)}`
    : formatCurrency(absoluteAmount)
}
